import React, {
  useEffect, useMemo, useRef,
} from 'react';

import type { EventStatisticRequest } from '@vlabs/api/artemy/model';
import { RefreshRightIcon, RotationLeftIcon } from '@vlabs/icons';
import { chartConfigurations } from '@vlabs/monokit/entities/charts/chart-form/chartDefaultConfigurations';
import { chartTypeOptions } from '@vlabs/monokit/entities/charts/chart-form/constants';
import { ClusteringForm } from '@vlabs/monokit/entities/charts/chart-form/forms/ClusteringForm.jsx';
import { CommonParamsForm } from '@vlabs/monokit/entities/charts/chart-form/forms/CommonParamsForm.jsx';
import { useChartFormTranslation } from '@vlabs/monokit/entities/charts/chart-form/i18n';
import type { DiagramType } from '@vlabs/monokit/entities/charts/chart-form/types';
import {
  Control, Divider, openConfirmPopup, Tab, Tabs,
} from '@vlabs/uikit';
import { useForm, FormProvider, useWatch } from 'react-hook-form';

import st from './ChartForm.module.sass';
import { FiltersForm } from './forms/FiltersForm';

export type StatisticType =
  | 'ageDistribution'
  | 'attendanceByGender'
  | 'genderDistribution'
  | 'genderRatio'
  | 'emotionDistribution'
  | 'averageAgeByGender';

type FormGetEventStats = Omit<EventStatisticRequest, 'group_by'> & {
  group_by: {
    type: string | null;
    count: number | string;
    timeDimension: string | null;
    frequency: string | null;
  };
};

interface StatsForm {
  getEventStats: FormGetEventStats;
  getTemplateStats: {
    name: string;
    diagramType: DiagramType;
  };
}

interface ChartFormProps {
  statisticType: StatisticType;
  onSubmit: (values: FormGetEventStats) => void;
  onChangeFormData: (formData: StatsForm) => void;
}

export function ChartForm({
  statisticType,
  onSubmit,
  onChangeFormData,
}: ChartFormProps): React.JSX.Element {
  const { t } = useChartFormTranslation();

  const defaultValues: StatsForm = chartConfigurations[statisticType];

  const form = useForm<StatsForm>({ defaultValues });
  const previousDiagramTypeRef = useRef(defaultValues.getTemplateStats.diagramType);
  const resetState = useRef({
    commonForm: false,
    clusteringForm: false,
  });

  useEffect(() => {
    form.setValue('getTemplateStats.name', chartTypeOptions?.[statisticType]?.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const watchedDiagramType = useWatch({ control: form.control, name: 'getTemplateStats.diagramType' });
  const groupByTypeValue = useWatch({ control: form.control, name: 'getEventStats.group_by.type' });
  const targets = useWatch({ control: form.control, name: 'getEventStats.targets' });

  const isPieOrDoughnutChartType = useMemo(() => {
    return (['pie', 'doughnut'] as DiagramType[]).includes(watchedDiagramType);
  }, [watchedDiagramType]);

  const requiresConfirmation = useMemo(() => {
    return groupByTypeValue ?? (targets && targets?.length > 2);
  }, [groupByTypeValue, targets]);

  const resetForm = async () => {
    resetState.current = {
      commonForm: true,
      clusteringForm: true,
    };

    form.reset(chartConfigurations[statisticType]);

    onChangeFormData(form.getValues());
    previousDiagramTypeRef.current = watchedDiagramType;
    onSubmit(form.getValues().getEventStats);
  };

  const resetFormAndFetchData = async (currentValues: StatsForm) => {
    const formWithResetGroupings = {
      ...currentValues,
      getEventStats: {
        ...currentValues.getEventStats,
        group_by: {
          type: null,
          count: '',
          timeDimension: null,
          frequency: null,
        },
        targets: [
          currentValues.getEventStats.targets?.[0],
          ...(defaultValues.getEventStats.targets?.slice(1) ?? []),
        ],
      },
    };

    form.reset(formWithResetGroupings);
    onChangeFormData(form.getValues());
    previousDiagramTypeRef.current = watchedDiagramType;
    onSubmit(form.getValues().getEventStats);
  };

  const handleDiagramTypeChange = () => {
    const currentValues = form.getValues();
    openConfirmPopup({
      title: t('diagram_type.change_title') as unknown as string,
      message: t('diagram_type.change_message'),
      type: 'warning',
      onConfirm: async () => resetFormAndFetchData(currentValues),
      onCancel: () => form.setValue('getTemplateStats.diagramType', previousDiagramTypeRef.current),
    });
  };

  useEffect(() => {
    if (isPieOrDoughnutChartType && requiresConfirmation) {
      handleDiagramTypeChange();
      return;
    }

    onChangeFormData(form.getValues());
    previousDiagramTypeRef.current = watchedDiagramType;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedDiagramType]);

  const context = {
    ...form,
    groupByTypeValue,
    isPieOrDoughnutChartType,
    resetState,
  };

  return (
    <FormProvider {...context}>
      <form onSubmit={form.handleSubmit((formData) => onSubmit(formData.getEventStats))}>
        <div className={st.TabsWrapper}>
          <Tabs testId="tabs">
            <Tab
              header={t('tabs.basic_parameters')}
              name="basicParameters"
            >
              <CommonParamsForm />
            </Tab>
            <Tab
              header={t('tabs.grouping')}
              name="grouping"
            >
              <ClusteringForm />
            </Tab>
            <Tab
              header={t('tabs.filters')}
              name="filters"
            >
              <FiltersForm />
            </Tab>
          </Tabs>
        </div>
        <div className={st.SettingsPanelButtonGroup}>
          <div className={st.ButtonWrapper}>
            <Control.Button
              data-testid="refreshButton"
              fullWidth
              icon={<RefreshRightIcon />}
              type="submit"
            >
              {t('btn.refresh')}
            </Control.Button>
          </div>
          <Divider small />
          <div className={st.ButtonWrapper}>
            <Control.Button
              data-testid="resetButton"
              fullWidth
              icon={<RotationLeftIcon />}
              kind="negative"
              onClick={resetForm}
            >
              {t('btn.reset_to_default')}
            </Control.Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
